import { Modal, Button, SearchBar } from "@legal-500/legal500-design-system";
import { useState } from "react";
import { platformService } from "../../api/platform.service";
import { useQuery } from "../../hooks/useApi";
import { useCreateGatewayRoute } from "../../hooks/useGatewayRoutes";
import LoadingState from "../../components/LoadingState";
import type { GatewayRouteEntry } from "../../api/gatewayRoutes.service";
import {
  flattenServices,
  addRouteCandidates,
  routeKey,
  type CatalogRoute,
} from "./addRouteCandidates";
import styles from "./GatewayAllowlist.module.css";

type AddRouteModalProps = {
  existingRoutes: GatewayRouteEntry[];
  onSuccess: () => void;
  onClose: () => void;
};

function methodBadgeClass(method: string): string {
  switch (method.toUpperCase()) {
    case "GET":
      return styles.methodGet;
    case "POST":
      return styles.methodPost;
    case "PUT":
      return styles.methodPut;
    case "PATCH":
      return styles.methodPatch;
    case "DELETE":
      return styles.methodDelete;
    default:
      return styles.methodDefault;
  }
}

export default function AddRouteModal({ existingRoutes, onSuccess, onClose }: AddRouteModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: servicesData, loading: servicesLoading } = useQuery(
    () => platformService.getServices(),
    [],
  );

  const createRoute = useCreateGatewayRoute();

  const catalog = servicesData ? flattenServices(servicesData.services) : [];
  const candidates = addRouteCandidates(catalog, existingRoutes);

  const filteredCandidates = searchTerm
    ? candidates.filter(
        r =>
          r.serviceName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.path.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : candidates;

  function toggleCandidate(candidate: CatalogRoute) {
    const key = routeKey(candidate.serviceName, candidate.method, candidate.path);
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function isSelected(candidate: CatalogRoute): boolean {
    return selected.has(routeKey(candidate.serviceName, candidate.method, candidate.path));
  }

  async function handleConfirm() {
    const toAdd = candidates.filter(c => selected.has(routeKey(c.serviceName, c.method, c.path)));
    if (toAdd.length === 0) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      for (const route of toAdd) {
        await createRoute.mutate({
          serviceName: route.serviceName,
          method: route.method,
          path: route.path,
        });
      }
      onSuccess();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to add routes");
      setSubmitting(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="Add route" size="mediumLarge">
      <div className={styles.modalBody}>
        {servicesLoading ? (
          <LoadingState />
        ) : (
          <>
            <div className={styles.modalSearch}>
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Filter by service or path..."
              />
            </div>
            {filteredCandidates.length === 0 ? (
              <p className={styles.modalEmpty}>
                {candidates.length === 0
                  ? "All catalog routes are already in the allowlist."
                  : "No routes match your search."}
              </p>
            ) : (
              <ul className={styles.candidateList} role="list">
                {filteredCandidates.map(candidate => {
                  const key = routeKey(candidate.serviceName, candidate.method, candidate.path);
                  const checked = isSelected(candidate);
                  return (
                    <li key={key} className={styles.candidateItem}>
                      <label className={styles.candidateLabel}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCandidate(candidate)}
                          className={styles.candidateCheckbox}
                        />
                        <span
                          className={`${styles.methodBadge} ${methodBadgeClass(candidate.method)}`}
                        >
                          {candidate.method.toUpperCase()}
                        </span>
                        <span className={styles.candidateService}>{candidate.serviceName}</span>
                        <code className={styles.pathCode} title={candidate.path}>
                          {candidate.path}
                        </code>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            {submitError && <p className={styles.modalError}>{submitError}</p>}
            <div className={styles.modalFooter}>
              <Button
                onClick={onClose}
                variant="outlined"
                color="default"
                disabled={submitting}
                aria-label="Cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                variant="contained"
                color="default"
                disabled={submitting || selected.size === 0}
                aria-label={`Add ${selected.size} route${selected.size !== 1 ? "s" : ""}`}
              >
                {submitting
                  ? "Adding..."
                  : `Add ${selected.size} route${selected.size !== 1 ? "s" : ""}`}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
